"use client";

import React from "react";
import { DashboardProvider } from "@/modules/dashboard/context/DashboardContext";
import { PageProvider } from "@/modules/pages/context/PageContext";
import { WidgetProvider } from "@/modules/widgets/context/WidgetContext";
import { WebSocketProvider } from "@/modules/websocket";
import { UserProvider } from "@/modules/auth/context/UserContext";
import { EditWidgetProvider } from "@/modules/widgets/context/EditWidgetContext";

interface AppProvidersWithWebSocketProps {
  children: React.ReactNode;
  websocketEnabled?: boolean;
  websocketUrl?: string;
}

/**
 * Enhanced App-wide context providers with WebSocket support
 * Organized by update frequency:
 * - UserProvider: Authentication and user context
 * - DashboardProvider: Dashboard state including dealer and date selections
 * - PageProvider: Medium frequency updates (page navigation, page management)
 * - WidgetProvider: High frequency updates (widget selection, drag/drop, positions)
 * - EditWidgetProvider: Widget editing state management
 * - WebSocketProvider: Real-time updates (agent communication, live data)
 */
export const AppProvidersWithWebSocket: React.FC<AppProvidersWithWebSocketProps> = ({ 
  children,
  websocketEnabled = true,
  websocketUrl
}) => {
  return (
    <UserProvider>
      <DashboardProvider>
        <PageProvider>
          <WidgetProvider>
            <EditWidgetProvider>
              <WebSocketProvider enabled={websocketEnabled} url={websocketUrl}>
                {children}
              </WebSocketProvider>
            </EditWidgetProvider>
          </WidgetProvider>
        </PageProvider>
      </DashboardProvider>
    </UserProvider>
  );
};
